import { useCallback, useMemo, useState } from 'react';

type MaintenanceItem = {
    gameSvcCd?: string;
    isMntn?: string;
    mntnStrtDtime?: string;
    mntnStopDtime?: string;
};

type UseMaintenanceModalGuardParams = {
    mntnData: MaintenanceItem[];
    targetGameSvcCd: string;
    langData?: any;
    specialModalStartKst?: string;
    specialModalEndKst?: string;
    specialModalMessage?: string;
};

const parseKstDateToUtcMs = (value?: string): number | null => {
    if (!value) {
        return null;
    }

    const normalized = value.trim().replace(' ', 'T');
    if (!normalized) {
        return null;
    }

    const hasTimezone = /([zZ]|[+-]\d{2}:\d{2})$/.test(normalized);
    const kstIso = hasTimezone ? normalized : `${normalized}+09:00`;
    const parsed = Date.parse(kstIso);

    return Number.isNaN(parsed) ? null : parsed;
};

const normalizeGameSvcCd = (value?: string): string => {
    const normalized = String(value || '').trim().toUpperCase();
    if (normalized === 'KRBBO') return 'BBO';
    return normalized;
};

const useMaintenanceModalGuard = ({
    mntnData,
    targetGameSvcCd,
    langData,
    specialModalStartKst,
    specialModalEndKst,
    specialModalMessage,
}: UseMaintenanceModalGuardParams) => {
    const [resultModalOpen, setResultModalOpen] = useState(false);
    const [resultModalMessage, setResultModalMessage] = useState('');

    const specialModalStartUtcMs = useMemo(
        () => parseKstDateToUtcMs(specialModalStartKst),
        [specialModalStartKst]
    );

    const specialModalEndUtcMs = useMemo(
        () => parseKstDateToUtcMs(specialModalEndKst),
        [specialModalEndKst]
    );

    const currentMntn = useMemo(() => {
        const normalizedTarget = normalizeGameSvcCd(targetGameSvcCd);
        return mntnData.find(
            (item) => normalizeGameSvcCd(item?.gameSvcCd) === normalizedTarget
        );
    }, [mntnData, targetGameSvcCd]);

    const isMaintenance = useMemo(() => {
        return !!currentMntn && currentMntn.isMntn === 'Y';
    }, [currentMntn]);

    const openResultModal = useCallback((message: string) => {
        setResultModalMessage(message);
        setResultModalOpen(true);
    }, []);

    const closeResultModal = useCallback(() => {
        setResultModalOpen(false);
    }, []);

    const openMaintenanceModal = useCallback(() => {
        const now = Date.now();
        const isAfterStart = specialModalStartUtcMs !== null && now >= specialModalStartUtcMs;
        const isBeforeEnd = specialModalEndUtcMs === null || now < specialModalEndUtcMs;
        const isSpecialModalTime = isAfterStart && isBeforeEnd;

        if (isSpecialModalTime) {
            openResultModal(
                specialModalMessage || langData?.RESP_MSG_GAME3112 || '서버 클로징입니다.'
            );
            return;
        }

        openResultModal(
            `${langData?.COMM_MAIN_MNTN_MAIN_LBL_00 || ''}\n${langData?.COMM_MAIN_MNTN_TIME_LBL_STRT_00 || ''} : ${currentMntn?.mntnStrtDtime || '-'}\n${langData?.COMM_MAIN_MNTN_TIME_LBL_STOP_00 || ''} : ${currentMntn?.mntnStopDtime || '-'}`
        );
    }, [currentMntn, langData, openResultModal, specialModalEndUtcMs, specialModalMessage, specialModalStartUtcMs]);

    const runWithMaintenanceGuard = useCallback(
        (onNotMaintenance: () => void) => {
            if (!isMaintenance) {
                onNotMaintenance();
                return;
            }

            openMaintenanceModal();
        },
        [isMaintenance, openMaintenanceModal]
    );

    return {
        isMaintenance,
        resultModalOpen,
        resultModalMessage,
        openResultModal,
        closeResultModal,
        runWithMaintenanceGuard,
    };
};

export default useMaintenanceModalGuard;
